import { Link } from 'react-router-dom';
import { Anchor, Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-slate-800">
        <div className="container-custom py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
              <p className="text-slate-400">Get the latest marine engine catalogs and industry news</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3 w-full lg:w-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 w-full lg:w-80"
                data-testid="newsletter-email"
              />
              <Button type="submit" className="btn-primary whitespace-nowrap" disabled={loading} data-testid="newsletter-submit">
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-orange-500 rounded-sm flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold">EngineShip</span>
                <span className="text-xl font-bold text-orange-500">Parts</span>
              </div>
            </Link>
            <p className="text-slate-400 mb-6">
              Your trusted source for premium marine engines and parts. 
              Over 100+ catalogs from leading manufacturers.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/inventory" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Engine Catalog
                </Link>
              </li>
              <li>
                <Link to="/inventory?is_featured=true" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Featured Engines
                </Link>
              </li>
              <li>
                <Link to="/inventory?is_new=true" className="text-slate-400 hover:text-orange-500 transition-colors">
                  New Arrivals
                </Link>
              </li>
              <li>
                <Link to="/compare" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Compare Engines
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Request Quote
                </Link>
              </li>
            </ul>
          </div>

          {/* Brands */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Top Brands</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/inventory?brand=Volvo Penta" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Volvo Penta
                </Link>
              </li>
              <li>
                <Link to="/inventory?brand=Caterpillar" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Caterpillar
                </Link>
              </li>
              <li>
                <Link to="/inventory?brand=Cummins" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Cummins
                </Link>
              </li>
              <li>
                <Link to="/inventory?brand=MAN" className="text-slate-400 hover:text-orange-500 transition-colors">
                  MAN
                </Link>
              </li>
              <li>
                <Link to="/inventory?brand=Yanmar" className="text-slate-400 hover:text-orange-500 transition-colors">
                  Yanmar
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-xs text-orange-400 font-medium">US Office</span>
                  <p className="text-slate-400">
                    750 Cliff Road<br />
                    Port Reading, NJ 07008, US
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-xs text-orange-400 font-medium">China Office</span>
                  <p className="text-slate-400">
                    501, Haoyuetianjin Community<br />
                    Haigang District, Qinhuangdao City<br />
                    Hebei Province, China
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <a href="tel:+12012344851" className="text-slate-400 hover:text-orange-500 transition-colors">
                  +1 (201) 234-4851
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <a href="mailto:sales@engineshippart.com" className="text-slate-400 hover:text-orange-500 transition-colors">
                  sales@engineshippart.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} EngineShipParts. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
